import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../lib/format';
import { formatMoney } from '../../../lib/money';
import { Barcode } from '../Barcode';
import { DocFrame, Row } from '../DocFrame';
import type { PrintDocProps } from '../print-types';
import { registerPrintDoc } from '../print-registry';
import type { ReceiptPrint } from '../types';

export function ReceiptDoc({ data, size, onReady }: PrintDocProps<ReceiptPrint>): ReactNode {
  const { t } = useTranslation();
  const hasBarcode = size === '80mm';
  const f = data.forWhat;
  return (
    <DocFrame title={t('print.title.receipt')} meta={data.meta} hasBarcode={hasBarcode} onReady={onReady}>
      <Row label={t('print.number')} value={data.receiptNo} />
      <Row label={t('print.dateTime')} value={formatDateTime(data.receivedAt)} />
      <section className="print-block">
        <div className="print-label">{t('print.customer')}</div>
        <div>{data.customerName ?? t('print.walkIn')}</div>
      </section>
      <section className="print-block">
        <div>
          {f.kind === 'INVOICE'
            ? t('print.paymentForInvoice', { invoiceNo: f.invoiceNo })
            : f.bike === null
              ? t('print.paymentForBookingNoBike', { bookingNo: f.bookingNo })
              : t('print.paymentForBooking', { bookingNo: f.bookingNo, bike: f.bike })}
        </div>
      </section>
      <section className="print-block print-totals">
        <Row label={t('print.method.label')} value={t(`print.method.${data.method}`)} />
        {data.reference === null ? null : <Row label={t('print.reference')} value={data.reference} />}
        <Row label={t('print.amount')} value={<strong>{formatMoney(data.amount)}</strong>} />
        {f.kind === 'INVOICE' ? <Row label={t('print.balanceAfter')} value={formatMoney(f.balanceAfter)} /> : null}
      </section>
      {data.receivedBy === null ? null : <div className="print-small">{t('print.receivedByStaff', { name: data.receivedBy })}</div>}
      {hasBarcode ? (
        <div className="print-barcode-wrap">
          <Barcode value={data.receiptNo} onReady={onReady} />
        </div>
      ) : null}
    </DocFrame>
  );
}

registerPrintDoc<ReceiptPrint>('receipt', ReceiptDoc);
